import {sequenceT} from 'fp-ts/lib/Apply';
import {Either, getOrElse, left, map as mapEither, mapLeft, right} from 'fp-ts/lib/Either';
import {eqString, getStructEq} from 'fp-ts/lib/Eq';
import {pipe} from 'fp-ts/lib/pipeable';
import {ToMatchOptions} from '../../Shared/utilities/MapDiscriminatedUnion';
import {applicativeValidation, lift} from '../../Shared/utilities/ToValidation';

const questionNotEmpty = (field: string): Either<string, string> =>
  field && field.length > 0 ? right(field) : left('Question must not be empty');
const answerNotEmpty = (field: string): Either<string, string> =>
  field && field.length > 0 ? right(field) : left('Answer must not be empty');

const questionNotEmptyV = lift(questionNotEmpty);
export const answerNotEmptyV = lift(answerNotEmpty);

const validateSecurityQuestion = (question: string, answer: string) =>
  pipe(
    sequenceT(applicativeValidation)(answerNotEmptyV(answer)),
    mapEither(() => [question, answer])
  );

export class UnvalidatedSecurityQuestion {
  public readonly key = 'Unvalidated';

  constructor(public readonly question: string, public readonly answer: string) {}
}

export class InvalidSecurityQuestion {
  public readonly key = 'Invalid';

  constructor(public readonly question: string, public readonly answer: string, public readonly errors: string[]) {}
}

export class ValidSecurityQuestion {
  public static create(question: string, answer: string): SecurityQuestion {
    const questionEither = pipe(
      validateSecurityQuestion(question, answer),
      mapLeft(errors => new InvalidSecurityQuestion(question, answer, errors)),
      mapEither(([q, a]) => new ValidSecurityQuestion(q, a))
    );

    return pipe(
      questionEither,
      getOrElse(invalidQuestion => invalidQuestion as SecurityQuestion)
    );
  }

  public readonly key = 'Valid';
  private constructor(public readonly question: string, public readonly answer: string) {}
}

export type SecurityQuestion = ValidSecurityQuestion | InvalidSecurityQuestion | UnvalidatedSecurityQuestion;

export type SecurityQuestionOptions<TReturn> = ToMatchOptions<SecurityQuestion, 'key', TReturn>;

export const matchSecurityQuestion = <TReturn>(
  securityQuestion: SecurityQuestion,
  options: SecurityQuestionOptions<TReturn>
): TReturn => {
  switch (securityQuestion.key) {
    case 'Invalid':
      return options.Invalid(securityQuestion);
    case 'Unvalidated':
      return options.Unvalidated(securityQuestion);
    case 'Valid':
      return options.Valid(securityQuestion);
  }
};

export const eqSecurityQuestion = getStructEq<SecurityQuestion>({
  question: eqString,
  answer: eqString,
  key: eqString,
});
